import { Toaster } from "@shadcn/ui/sonner";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode, useEffect, useLayoutEffect, useState } from "react";
import AuthService, { AuthenticationStatus } from "@/src/application/services/auth-service";
import UserService from "@/src/application/services/user-service";
import AppState from "@/src/application/store/app-state";
import { BrandProvider, useBrand, useBrandUpdate } from "@/src/brand";
import { default as brandConfig } from "@/src/config/brand";
import { buildBackendUrl } from "@/src/config/runtime-url";
import { applyBrandTheme } from "@/src/config/theme";
import { ErrorBoundary } from "@/src/presentation/components/error-boundary";
import ServiceProvider from "@/src/presentation/components/providers/service-provider";
import { useInject } from "@/src/presentation/hooks/useInject";
import { createAppRouter } from "./router";

function App() {
  return (
    <StrictMode>
      <ErrorBoundary>
        <ServiceProvider>
          <BrandProvider>
            <BrandInit />
            <AppRouter />
            <Toaster richColors />
          </BrandProvider>
        </ServiceProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}

function BrandInit() {
  const brand = useBrand();
  const updateBrand = useBrandUpdate();

  useEffect(() => {
    document.title = brand.name;
  }, [brand.name]);
  useLayoutEffect(() => {
    if (brand.loginBg) {
      document.documentElement.style.setProperty("--login-bg-image", `url('${brand.loginBg}')`);
    }
  }, [brand.loginBg]);
  useLayoutEffect(() => {
    applyBrandTheme(brand.theme);
  }, [brand.theme]);
  useLayoutEffect(() => {
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    if (link && brand.favicon) {
      link.href = brand.favicon;
    }
  }, [brand.favicon]);

  // Fetch dynamic council name from backend config
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount; updateBrand is recreated after brand updates.
  useEffect(() => {
    const apiUrl =
      (window as unknown as { __CONFIG__?: Record<string, string> }).__CONFIG__?.API_URL ||
      brandConfig.apiUrl;
    fetch(buildBackendUrl(apiUrl, "/api/v1/config"))
      .then((res) => res.json())
      .then((data: { councilName?: string }) => {
        if (data.councilName) {
          updateBrand({ strings: { councilName: data.councilName } } as Partial<
            typeof brandConfig
          >);
        }
      })
      .catch(() => {
        // Silent — use static fallback from brandConfig
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps — run once on mount
  }, []);

  return null;
}

function AppRouter() {
  const appState = useInject(AppState);
  const authService = useInject(AuthService);
  const userService = useInject(UserService);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize auth status
    authService.setStatus(AuthenticationStatus.FetchingUserInfo);
    appState.setLoading(true);

    userService
      .loadUserInfo()
      .then((isSuccess) => {
        authService.setStatus(
          isSuccess ? AuthenticationStatus.Authenticated : AuthenticationStatus.NotAuthenticated
        );
      })
      .catch(() => {
        authService.setStatus(AuthenticationStatus.NotAuthenticated);
      })
      .finally(() => {
        appState.setLoading(false);
        setIsInitialized(true);
      });
  }, [appState, authService, userService]);

  // Show loading spinner while initializing
  if (!isInitialized) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="size-16 animate-spin rounded-full border-8 border-dashed border-brand-accent" />
      </div>
    );
  }

  const router = createAppRouter();

  return <RouterProvider router={router} />;
}

export { App };
